import React, { useEffect, useState } from 'react'
import { useEvent } from '@/hooks/useEvent'
import { supabase } from '@/lib/supabaseClient'
import type { CompanyRow, HotelRow, RoomTypeRow } from '@/lib/types'
import { Button, Card, Checkbox, Select, useToast } from '@/components/ui'
import { buildMasterTemplate } from '@/lib/excel/masterTemplate'
import { buildRoomingListExport, buildHotelExport, buildCompanyExport, buildLinksExport } from '@/lib/excel/exportBuilder'
import { parseWorkbookFile } from '@/lib/excel/shared'
import { buildImportPreview, ImportPreview } from '@/lib/excel/importPreview'
import { applyImport, attachKnownIds } from '@/lib/excel/applyImport'
import ImportPreviewPanel, { importHasBlockingIssues } from '@/components/import/ImportPreviewPanel'

/** Event-level Import / Export center (sections 33-42, 68-70, 87): the master bulk workflow plus every export an admin needs, all in one place. */
export default function ImportExport() {
  const { event } = useEvent()
  const { push } = useToast()
  const [hotels, setHotels] = useState<HotelRow[]>([])
  const [roomTypes, setRoomTypes] = useState<RoomTypeRow[]>([])
  const [companies, setCompanies] = useState<CompanyRow[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [selectedHotelId, setSelectedHotelId] = useState('')
  const [selectedCompanyId, setSelectedCompanyId] = useState('')

  const [fileName, setFileName] = useState('')
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [bypassDuplicates, setBypassDuplicates] = useState(false)
  const [applying, setApplying] = useState(false)

  useEffect(() => {
    (async () => {
      const [{ data: h }, { data: rt }, { data: c }] = await Promise.all([
        supabase.from('hotels').select('*').eq('event_id', event.id).eq('is_active', true).order('name'),
        supabase.from('room_types').select('*').eq('event_id', event.id).eq('is_active', true).order('name'),
        supabase.from('companies').select('*').eq('event_id', event.id).is('archived_at', null).order('name'),
      ])
      setHotels((h as HotelRow[]) || [])
      setRoomTypes((rt as RoomTypeRow[]) || [])
      setCompanies((c as CompanyRow[]) || [])
    })()
  }, [event.id])

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key)
    try {
      await fn()
    } catch (e: any) {
      push(e.message || 'Something went wrong', 'error')
    } finally {
      setBusy(null)
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setFileName(file.name)
    setBusy('parsing')
    try {
      const sheets = await parseWorkbookFile(file)
      const companyCodes = new Set(companies.map((c) => c.code))
      let p = buildImportPreview(sheets, { hotels, roomTypes, companyCodes })
      p = attachKnownIds(p, hotels, roomTypes)
      setPreview(p)
    } catch (err: any) {
      push(err.message || 'Could not read that file', 'error')
    } finally {
      setBusy(null)
    }
  }

  async function apply() {
    if (!preview) return
    setApplying(true)
    const result = await applyImport({ eventId: event.id, companyId: null, fileName, workflow: 'master', preview, bypassDuplicates })
    setApplying(false)
    if (!result.success) {
      push(result.error_detail || result.error_code || 'Import failed', 'error')
      return
    }
    push(
      `Import applied — ${result.companies_created ?? 0} new compan${result.companies_created === 1 ? 'y' : 'ies'}, ${result.companies_updated ?? 0} updated, ${result.allocations_applied ?? 0} allocation(s), ${result.guests_written ?? 0} guest(s) written`
    )
    setPreview(null)
    setFileName('')
    const { data: c } = await supabase.from('companies').select('*').eq('event_id', event.id).is('archived_at', null).order('name')
    setCompanies((c as CompanyRow[]) || [])
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-slate-900">Import / Export</h1>
        <p className="text-sm text-slate-500">Bulk-import companies, allocations and guests from Excel, or export the current state of this event.</p>
      </div>

      <div className="space-y-6">
        <Card className="p-5">
          <h3 className="mb-1 text-sm font-semibold text-slate-900">1. Download a Template</h3>
          <p className="mb-3 text-sm text-slate-500">Fill this in and upload it below. It includes worked examples and a Read Me sheet.</p>
          <Button variant="secondary" loading={busy === 'template'} onClick={() => run('template', () => buildMasterTemplate(event.name))}>
            Download Master Import Template
          </Button>
        </Card>

        <Card className="p-5">
          <h3 className="mb-1 text-sm font-semibold text-slate-900">2. Master Bulk Import</h3>
          <p className="mb-3 text-sm text-slate-500">Upload a file covering multiple companies at once. You'll see a full preview — nothing is saved until you click Apply Import.</p>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50">
            <input type="file" accept=".xlsx" className="hidden" onChange={handleFile} />
            {busy === 'parsing' ? 'Reading file…' : fileName || 'Choose an .xlsx file…'}
          </label>

          {preview && (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <ImportPreviewPanel preview={preview} hotels={hotels} roomTypes={roomTypes} onChangePreview={setPreview} />
              {preview.companies.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto rounded border border-slate-200 text-xs">
                  <table className="w-full">
                    <thead className="bg-slate-50 text-left text-slate-500">
                      <tr>
                        <th className="px-2 py-1">Company</th>
                        <th className="px-2 py-1">Code</th>
                        <th className="px-2 py-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.companies.map((c) => (
                        <tr key={c.code} className="border-t border-slate-100">
                          <td className="px-2 py-1">{c.name}</td>
                          <td className="px-2 py-1">{c.code}</td>
                          <td className="px-2 py-1">{c.isNew ? <span className="text-emerald-600">new</span> : <span className="text-slate-400">existing — updated</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <Checkbox label="Allow duplicate mobile numbers (admin override)" checked={bypassDuplicates} onChange={(e) => setBypassDuplicates(e.target.checked)} />
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setPreview(null)
                      setFileName('')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button variant="primary" loading={applying} disabled={importHasBlockingIssues(preview)} onClick={apply}>
                    Apply Import
                  </Button>
                </div>
              </div>
              {importHasBlockingIssues(preview) && <p className="mt-2 text-xs text-red-600">Fix all errors and resolve all unrecognized names before applying.</p>}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">Exports</h3>
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm text-slate-600">Final rooming list — every company, every hotel.</p>
              <Button variant="secondary" loading={busy === 'export-all'} onClick={() => run('export-all', () => buildRoomingListExport({ eventId: event.id }, event.name))}>
                Export Full Rooming List (.xlsx)
              </Button>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <p className="mb-2 text-sm text-slate-600">One hotel's rooming list.</p>
              <div className="flex flex-wrap items-center gap-2">
                <Select className="max-w-xs" value={selectedHotelId} onChange={(e) => setSelectedHotelId(e.target.value)}>
                  <option value="">Choose a hotel…</option>
                  {hotels.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                    </option>
                  ))}
                </Select>
                <Button
                  variant="secondary"
                  loading={busy === 'export-hotel'}
                  disabled={!selectedHotelId}
                  onClick={() => {
                    const h = hotels.find((x) => x.id === selectedHotelId)
                    if (!h) return
                    run('export-hotel', () => buildHotelExport({ eventId: event.id, hotelId: h.id }, h.name, event.name))
                  }}
                >
                  Export Hotel Rooming List
                </Button>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <p className="mb-2 text-sm text-slate-600">One company's rooming list.</p>
              <div className="flex flex-wrap items-center gap-2">
                <Select className="max-w-xs" value={selectedCompanyId} onChange={(e) => setSelectedCompanyId(e.target.value)}>
                  <option value="">Choose a company…</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                <Button
                  variant="secondary"
                  loading={busy === 'export-company'}
                  disabled={!selectedCompanyId}
                  onClick={() => {
                    const c = companies.find((x) => x.id === selectedCompanyId)
                    if (!c) return
                    run('export-company', () => buildCompanyExport({ eventId: event.id, companyId: c.id }, c.name, event.name))
                  }}
                >
                  Export Company Rooming List
                </Button>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <p className="mb-2 text-sm text-slate-600">A single sheet with every company's portal link, status and deadline — handy for a bulk email merge.</p>
              <Button variant="secondary" loading={busy === 'export-links'} onClick={() => run('export-links', () => buildLinksExport(event.id, event.name))}>
                Export Company Links
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
